import React, { useState, useEffect } from 'react';
import {
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Slider,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import FilterChip from 'src/App/Components/Chips/FilterChip';
import { FilterVal } from 'src/interfaces/iproptypes';
import { setIndividualRevenuePotentialRange, updatePriceTiers } from 'src/store/Dashboard/actions';
import { PriceTiers } from 'src/interfaces/idashboard';
import { COLOR_PRIMARY, COLOR_RANGE_AIRBNB_MARKERS, DEFAULT_FILTER_VALUES, exchangeRates, PRICE_TIERS, rating_marks } from 'src/const';
import styles from './styles';
import FilterActionButton from './FilterActionButton';
import { abbreviateNumber, convertCurrency } from 'src/utils/functions';
import { useCannotAccessFilter } from 'src/hooks/useFeatureAccess';

interface PerformanceFilterProps {
  priceTiers: PriceTiers;
  setFilterVal: React.Dispatch<React.SetStateAction<FilterVal>>;
  filterVal: FilterVal;
  currency: string;
  isMobileView: boolean;
  setTotalFilterApplied: React.Dispatch<React.SetStateAction<number>>;
  handleCloseMenu: () => void;
  resetFilter: (filterNamesToReset: string[]) => void;
  revenuePotentialTiersDistribution: any;
  isUserAllowed: boolean;
  handleOnSampleClick: (plan: string) => void;
  UpgradeCTA: (closeMenu: () => void) => JSX.Element;
  FilterCoingSoonComponent: React.ReactNode | null;
} 

const PerformanceFilter: React.FC<PerformanceFilterProps> = ({
  priceTiers,
  setFilterVal,
  filterVal,
  currency,
  setTotalFilterApplied,
  handleCloseMenu,
  resetFilter,
  revenuePotentialTiersDistribution,
  isUserAllowed,
  handleOnSampleClick,
  UpgradeCTA,
  FilterCoingSoonComponent
}) => {
  const dispatch = useDispatch();
  const [rating, setRating] = useState<number[]>(filterVal.rating || [3.9, 5]);
  const isAnyPriceTierChecked = filterVal.priceTierFilter.length > 0;

  const isAccessDenied = useCannotAccessFilter('performance');

  useEffect(() => {
    let appliedCount = 0;

    // Check priceTierFilter: active if any specific tier is toggled
    if (isAnyPriceTierChecked) {
      appliedCount++;
    }

    // Check hostType: active if not 'both'
    if (filterVal.hostType !== 'both') {
      appliedCount++;
    }

    // Check rating: active if not default [3.9, 5]
    // Ensure filterVal.rating exists and has 2 elements before comparing
    if (
      filterVal.rating &&
      filterVal.rating.length === 2 &&
      (filterVal.rating[0] !== DEFAULT_FILTER_VALUES.rating[0] || filterVal.rating[1] !== DEFAULT_FILTER_VALUES.rating[1])
    ) {
      appliedCount++;
    }

    // Check number_of_reviews: active if not 'all'
    if (filterVal.number_of_reviews !== 'all') {
      appliedCount++;
    }

    // Check revenueFilter: active if not 'all'
    if (filterVal.revenueFilter.length > 0) {
      appliedCount++;
    }

    setTotalFilterApplied(appliedCount);
  }, [filterVal, priceTiers, setTotalFilterApplied]);

  const ratingOptions = Array.from({ length: 12 }, (_, i) => 3.9 + i * 0.1).map(
    v => Math.round(v * 10) / 10
  );

  const handlePriceTierFilter = (value: string) => {
    // const updatedPriceTiers = getUpdatedPriceTiers(priceTiers, value);
    // const newFilterValues = getUpdatedFilterValue(updatedPriceTiers);

    let newPriceTiers = [...filterVal.priceTierFilter]
    if(value === 'all'){
      newPriceTiers = [];
    } else {      
      if((newPriceTiers as string[]).includes(value)){
        newPriceTiers = newPriceTiers.filter(tier => tier !== value);
      } else {
        (newPriceTiers as string[]).push(value);
      }
    }

    // dispatch(updatePriceTiers(newPriceTiers));
    setFilterVal(prev => ({ ...prev, priceTierFilter: newPriceTiers }));
  };

  const handleHostTypeChange = (value: 'independent' | 'professional' | 'both') => {
    setFilterVal(prev => ({ ...prev, hostType: value }));
  };

  const handleNumberOfReviewsChange = (value: string | 'all') => {
    setFilterVal(prev => ({ ...prev, number_of_reviews: value }));
  };

  const handleRatingChange = (type: 'min' | 'max', newValue: string | number) => {
    const value = Number(newValue);
    let currentRating = [...rating];

    if (type === 'min') {
      currentRating[0] = value;
      if (currentRating[0] > currentRating[1]) {
        currentRating[1] = Math.min(5, Math.max(value, currentRating[1]));
      }
    } else {
      // type === 'max'
      currentRating[1] = value;
      if (currentRating[1] < currentRating[0]) {
        currentRating[0] = Math.max(3.9, Math.min(value, currentRating[0]));
      }
    }
    // Ensure min is at least 3.9 and max is at most 5, and min <= max
    currentRating[0] = Math.max(3.9, currentRating[0]);
    currentRating[1] = Math.min(5, currentRating[1]);
    if (currentRating[0] > currentRating[1]) {
      if (type === 'min') currentRating[1] = currentRating[0];
      else currentRating[0] = currentRating[1];
    }

    setRating(currentRating);
    setFilterVal(prev => ({ ...prev, rating: currentRating }));
  };

  const handleRevenueFilter = (value: number | 'all') => {
    if (isAccessDenied) return;

    if (value === 'all') {
      return setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, revenueFilter: [] };
      });
    }

    if (isNaN(Number(value))) return null;

    let newValue: number[] = filterVal.revenueFilter as number[];

    if(newValue.includes(value)) {
      newValue.splice(newValue.indexOf(value), 1);
    } else {
      newValue.push(value);
    }    

    // don't save this setting to localStorage because it can change when we select different mMrkets
    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, revenueFilter: newValue };
    });

  };

  return (
    <Grid
      container
      direction="column"
      sx={{ maxWidth: 400, px: 2.5, py: 1, zIndex: 999999, position: 'relative' }}
    >
      {!isUserAllowed && UpgradeCTA(handleCloseMenu)}
      {isUserAllowed && FilterCoingSoonComponent && FilterCoingSoonComponent}
      <Grid item>
        <Typography variant="body1" style={styles.filterTitleStyle}>
          Price Tiers
        </Typography>
        <Grid container spacing={1}>
          <Grid item>
            <FilterChip
              value="all"
              label={<CenteredLabel text="Any" />}
              handleOnClick={() => handlePriceTierFilter('all')}
              variant={filterVal.priceTierFilter.length === 0 ? 'filled' : 'outlined'}
              styles={chipStyles}
            />
          </Grid>
          {PRICE_TIERS.map((tier, index) => (
            <Grid item key={tier}>
              <FilterChip
                value={tier.toLowerCase()}
                label={<PriceLabel tier={tier} index={index} currency={currency} />}
                handleOnClick={() => handlePriceTierFilter(tier.toLowerCase())}
                variant={(filterVal.priceTierFilter as string[]).includes(tier.toLowerCase()) ? 'filled' : 'outlined'}
                styles={chipStyles}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>

      <Grid item sx={{ mt: 3 }}>
        <FormControl variant="outlined" size="small" fullWidth>
        <Typography variant="body1" style={styles.filterTitleStyle}>
            Revenue Filter
          </Typography>
          <Grid xs={12} container justifyContent="flex-start" style={{ gap: '5px' }}>
            <FilterChip
              value={'all'}
              label={'Any'}
              handleOnClick={() => handleRevenueFilter('all')}
              variant={filterVal.revenueFilter.length === 0 ? 'filled' : 'outlined'}
            />
            {/* {revenuePotentialTiersDistribution && revenuePotentialTiersDistribution */}
            {
              [25, 50, 75, 100]
              .map(Number)
              .sort((a, b) => a - b)
              .map((revPotential, index) => {
                return (
                  <FilterChip
                    value={revPotential}
                    label={
                      <>
                        <Typography
                          variant="caption"
                          component="p"
                          style={{
                            fontWeight: 'bold',
                          }}
                        >
                          {/* {'<='} {exchangeRates[currency].symbol}{' '} */}
                          {/* {abbreviateNumber(convertCurrency(currency, revPotential))} */}
                          {'<= '}{revPotential + ' th percentile'}
                        </Typography>
                      </>
                    }
                    handleOnClick={() => {
                      handleRevenueFilter(revPotential);
                    }}
                    variant={
                      (filterVal.revenueFilter.includes(revPotential))
                        ? 'filled'
                        : 'outlined'
                    }
                    styles={{
                      backgroundColor: 'transparent',
                      borderColor: `${COLOR_RANGE_AIRBNB_MARKERS[index]}`,
                      color: `#000`,
                      '&.MuiChip-filled': {
                        backgroundColor: `${COLOR_RANGE_AIRBNB_MARKERS[index]}60`,
                        border: `1px solid ${COLOR_RANGE_AIRBNB_MARKERS[index]}`,
                      },
                    }}
                  />
                );
              })}
          </Grid>
        </FormControl>
      </Grid>

      <Grid item sx={{ mt: 3 }}>
        <Typography style={styles.filterTitleStyle}>Professionally Managed</Typography>
        <FormControl fullWidth>
          <RadioGroup
            row
            value={filterVal.hostType}
            onChange={e =>
              handleHostTypeChange(e.target.value as 'independent' | 'professional' | 'both')
            }
          >
            <FormControlLabel
              value="professional"
              control={<Radio sx={{ color: COLOR_PRIMARY }} />}
              label="Yes"
            />
            <FormControlLabel
              value="independent"
              control={<Radio sx={{ color: COLOR_PRIMARY }} />}
              label="No"
            />
            <FormControlLabel
              value="both"
              control={<Radio sx={{ color: COLOR_PRIMARY }} />}
              label="Both"
            />
          </RadioGroup>
        </FormControl>
      </Grid>

      <Grid item sx={{ mt: 3 }}>
        <Typography style={styles.filterTitleStyle}>Rating</Typography>
        <Grid container alignItems="center" sx={{ mt: 1 }}>
          <Grid item>
            <Select
              variant="outlined"
              size="small"
              value={rating[0]}
              onChange={e => handleRatingChange('min', e.target.value)}
              MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
              sx={styles.rangeButtonStyle}
            >
              {ratingOptions.map(opt => (
                <MenuItem key={`min-rating-${opt}`} value={opt}>
                  {opt === 3.9 ? '<4.0' : opt.toFixed(1)}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item>
            <Typography sx={{ mx: 1 }}>-</Typography>
          </Grid>
          <Grid item>
            <Select
              variant="outlined"
              size="small"
              value={rating[1]}
              onChange={e => handleRatingChange('max', e.target.value)}
              MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
              sx={styles.rangeButtonStyle}
            >
              {ratingOptions.map(opt => (
                <MenuItem key={`max-rating-${opt}`} value={opt}>
                  {opt.toFixed(1)}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </Grid>

      <Grid item sx={{ mt: 3 }}>
        <Typography style={styles.filterTitleStyle}>Number of Reviews</Typography>
        <FormControl fullWidth>
          <RadioGroup
            row
            value={filterVal.number_of_reviews}
            onChange={e =>
              handleNumberOfReviewsChange(e.target.value === 'all' ? 'all' : e.target.value)
            }
          >
            <FormControlLabel
              value="all"
              control={<Radio sx={{ color: COLOR_PRIMARY }} />}
              label="Any"
            />
            {[1, 3, 10, 30].map(val => (
              <FormControlLabel
                key={val}
                value={val + '+' }
                control={<Radio sx={{ color: COLOR_PRIMARY }} />}
                label={`${val}+`}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>

      <Grid item sx={{ mt: 3 }}>
        <FilterActionButton
          isUserAllowed={isUserAllowed}
          handleCloseMenu={handleCloseMenu}
          handleOnSampleClick={handleOnSampleClick}
          resetFilter={() =>
            resetFilter(['priceTierFilter', 'hostType', 'rating', 'number_of_reviews', 'revenueFilter'])
          }
        />
      </Grid>
    </Grid>
  );
};

// --- Inline Components ---
const chipStyles = {
  width: '100px',
  height: '80px',
  ...styles.rangeButtonStyle,
};

const CenteredLabel = ({ text }: { text: string }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <Typography variant="body1">{text}</Typography>
  </div>
);

const PriceLabel = ({
  tier,
  index,
  currency,
}: {
  tier: string;
  index: number;
  currency: string;
}) => {
  const symbol = exchangeRates[currency]?.symbol || '$';
  const displaySymbol = symbol.length < 2 ? symbol : '$';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography variant="body1">
        {displaySymbol.repeat(index + 1)}
        <span style={{ color: '#ccc' }}>{displaySymbol.repeat(5 - (index + 1))}</span>
      </Typography>
      <Typography variant="body1">{tier}</Typography>
    </div>
  );
};

// --- Logic Helpers ---
const getUpdatedPriceTiers = (tiers: PriceTiers, value: string): PriceTiers => {
  if (value === 'all') {
    return Object.fromEntries(
      Object.entries(tiers).map(([tier, data]) => [tier, { ...data, toggled: false }])
    ) as PriceTiers;
  }

  return {
    ...tiers,
    [value]: { ...tiers[value], toggled: !tiers[value].toggled },
  };
};

const getUpdatedFilterValue = (tiers: PriceTiers): (number[] | 'all')[] => {
  const selected = Object.values(tiers)
    .filter(t => t.toggled)
    .map(t => t.range);

  return selected.length > 0 ? selected : ['all'];
};

export default PerformanceFilter;
